import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAsync } from "../hooks/useAsync";
import {
  completeSession,
  deleteSession,
  getSession,
  logSet,
  startSession,
  submitFeedback,
  submitRpe,
} from "../api/endpoints";
import {
  DIFFICULTY_LABELS,
  DIFFICULTY_RATINGS,
  FEELING_LABELS,
  FEELING_RATINGS,
  SORENESS_LABELS,
  SORENESS_RATINGS,
  SESSION_STATUS_LABELS,
  formatTarget,
  sessionStatus,
  type DifficultyRating,
  type FeelingRating,
  type SessionSet,
  type SorenessRating,
} from "../api/types";
import { Button, Card, ErrorMessage, PageHeading, Spinner } from "../components/ui";

/**
 * Summarises a logged set. Reps and duration are alternatives rather than both
 * being present, and weight is absent for bodyweight work, so each part is
 * included only when the API actually recorded it.
 */
function describeSet(set: SessionSet): string {
  const effort =
    set.repsCompleted != null
      ? `${set.repsCompleted} reps`
      : set.durationCompletedSeconds != null
        ? `${set.durationCompletedSeconds}s`
        : "logged";

  return set.weightUsedKg != null ? `${effort} @ ${set.weightUsedKg} kg` : effort;
}

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-600 focus:outline-none dark:border-[#2e2e2e] dark:bg-[#111111] dark:text-gray-100";

function ChoiceRow<T extends string>({
  label,
  options,
  labels,
  value,
  onChange,
  testId,
}: {
  label: string;
  options: readonly T[];
  labels: Record<T, string>;
  value: T | null;
  onChange: (value: T) => void;
  testId: string;
}) {
  return (
    <div className="mt-4">
      <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            aria-pressed={value === option}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              value === option
                ? "bg-emerald-600 text-white"
                : "border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-[#2e2e2e] dark:text-gray-300 dark:hover:bg-[#222]"
            }`}
            data-testid={`${testId}-${option}`}
          >
            {labels[option]}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SessionDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: session, error, loading, reload } = useAsync(() => getSession(id), [id]);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [effort, setEffort] = useState(5);
  const [notes, setNotes] = useState("");
  const [difficulty, setDifficulty] = useState<DifficultyRating | null>(null);
  const [feeling, setFeeling] = useState<FeelingRating | null>(null);
  const [soreness, setSoreness] = useState<SorenessRating | null>(null);
  const [feedbackSaved, setFeedbackSaved] = useState(false);

  async function act(fn: () => Promise<unknown>, after: "reload" | "home" = "reload") {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      if (after === "home") navigate("/sessions");
      else reload();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  // Sets arrive as one flat list for the whole session, so they are grouped
  // by exercise once rather than filtered inside the render loop. Ordering by
  // round keeps the badges in the sequence the user logged them.
  const setsByExercise = useMemo(() => {
    const grouped = new Map<string, SessionSet[]>();
    for (const set of session?.sets ?? []) {
      const existing = grouped.get(set.sessionExerciseId);
      if (existing) existing.push(set);
      else grouped.set(set.sessionExerciseId, [set]);
    }
    for (const sets of grouped.values()) sets.sort((a, b) => a.roundNumber - b.roundNumber);
    return grouped;
  }, [session]);

  if (loading) return <Spinner label="Loading session" />;
  if (error) return <ErrorMessage error={error} onRetry={reload} />;
  if (!session) return null;

  // The API stores no status column, so state is derived from the timestamps.
  const status = sessionStatus(session);
  const isPlanned = status === "planned";
  const isActive = status === "in_progress";
  const isComplete = status === "completed";

  return (
    <div data-testid="session-detail-page">
      <PageHeading title={session.sessionName ?? "Training session"} />

      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
          <span data-testid="session-status">{SESSION_STATUS_LABELS[status]}</span>
          <span>{session.rounds} rounds</span>
        </div>

        <ol className="mt-4 divide-y divide-gray-100 dark:divide-[#2e2e2e]">
          {(session.exercises ?? []).map((ex) => {
            const logged = setsByExercise.get(ex.id) ?? [];
            // The planner prescribes one set per round, so the next set is the
            // next unlogged round. Without this the round and set numbers were
            // fixed at 1, and the API — correctly — treats a repeat of the
            // same round and set as the same set, so every click after the
            // first silently overwrote the first one.
            const nextRound = logged.length + 1;
            const roundsRemaining = nextRound <= session.rounds;

            return (
              <li key={ex.id} className="py-3" data-testid={`session-exercise-${ex.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {ex.exerciseName}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {formatTarget(ex)}
                  </span>
                </div>

                {logged.length > 0 && (
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">
                    {logged.map((set) => (
                      <li
                        key={set.id}
                        className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                        data-testid={`session-set-${set.id}`}
                      >
                        {describeSet(set)}
                      </li>
                    ))}
                  </ul>
                )}

                {isActive &&
                  (roundsRemaining ? (
                    <button
                      onClick={() =>
                        void act(() =>
                          logSet(session.id, {
                            sessionExerciseId: ex.id,
                            roundNumber: nextRound,
                            setNumber: 1,
                            repsCompleted: ex.targetReps ?? null,
                            weightUsedKg: ex.suggestedWeightKg ?? null,
                            durationCompletedSeconds: ex.targetDurationSeconds ?? null,
                          }),
                        )
                      }
                      disabled={busy}
                      className="mt-2 text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                      data-testid={`session-logset-${ex.id}`}
                    >
                      Log round {nextRound} of {session.rounds}
                    </button>
                  ) : (
                    <p
                      className="mt-2 text-xs text-gray-500 dark:text-gray-500"
                      data-testid={`session-exercise-done-${ex.id}`}
                    >
                      All {session.rounds} rounds logged
                    </p>
                  ))}
              </li>
            );
          })}
        </ol>

        {actionError && (
          <div className="mt-4">
            <ErrorMessage error={actionError} />
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          {isPlanned && (
            <Button
              onClick={() => void act(() => startSession(session.id))}
              disabled={busy}
              data-testid="session-button-start"
            >
              Start session
            </Button>
          )}
          {isActive && (
            <Button
              onClick={() =>
                void act(() => completeSession(session.id, { userNotes: notes || null }))
              }
              disabled={busy}
              data-testid="session-button-complete"
            >
              Complete session
            </Button>
          )}
          {!isComplete && (
            <Button
              variant="danger"
              onClick={() => void act(() => deleteSession(session.id), "home")}
              disabled={busy}
              data-testid="session-button-delete"
            >
              Delete
            </Button>
          )}
        </div>

        {isActive && (
          <div className="mt-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Notes
            </label>
            <textarea
              id="notes"
              rows={3}
              maxLength={5000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass}
              data-testid="session-input-notes"
            />
          </div>
        )}
      </Card>

      {isComplete && session.perceivedEffort == null && (
        <Card className="mt-4 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            How hard was that?
          </h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            RPE drives weight progression and fatigue, so this is worth answering honestly.
          </p>
          <input
            type="range"
            min={1}
            max={10}
            value={effort}
            onChange={(e) => setEffort(Number(e.target.value))}
            className="mt-3 w-full accent-emerald-600"
            aria-label="Perceived effort"
            data-testid="session-input-rpe"
          />
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            RPE <span className="font-semibold">{effort}</span> / 10
          </p>
          <Button
            className="mt-3"
            onClick={() => void act(() => submitRpe(session.id, { perceivedEffort: effort }))}
            disabled={busy}
            data-testid="session-button-submitrpe"
          >
            Submit RPE
          </Button>
        </Card>
      )}

      {isComplete && (
        <Card className="mt-4 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Feedback</h2>

          <ChoiceRow
            label="Overall difficulty"
            options={DIFFICULTY_RATINGS}
            labels={DIFFICULTY_LABELS}
            value={difficulty}
            onChange={setDifficulty}
            testId="session-difficulty"
          />
          <ChoiceRow
            label="How do you feel now?"
            options={FEELING_RATINGS}
            labels={FEELING_LABELS}
            value={feeling}
            onChange={setFeeling}
            testId="session-feeling"
          />
          <ChoiceRow
            label="Soreness"
            options={SORENESS_RATINGS}
            labels={SORENESS_LABELS}
            value={soreness}
            onChange={setSoreness}
            testId="session-soreness"
          />

          <div className="mt-4 flex items-center gap-3">
            <Button
              onClick={() =>
                void act(async () => {
                  await submitFeedback(session.id, {
                    overallDifficulty: difficulty,
                    postSessionFeeling: feeling,
                    sorenessLevel: soreness,
                    readyToTrainAgain: null,
                  });
                  setFeedbackSaved(true);
                })
              }
              disabled={busy}
              data-testid="session-button-submitfeedback"
            >
              Save feedback
            </Button>
            {feedbackSaved && (
              <span className="text-sm text-emerald-700 dark:text-emerald-400" data-testid="session-feedback-saved">
                Saved
              </span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
